
import AppFloating from './AppFloating.vue';
import AppIcon from './AppIcon.vue';
import AddressSelector from './AddressSelector.vue';
import SdButton from './SdButton.vue';
import sdPopover from '@smartdesign/vue/components/popover';
import AddressItem from '../types/addressItem';
import FlatAddress from '../types/flatAddress';
import { defineComponent } from 'vue';

const errorMessage = 'Bitte Adresse angeben und aus der Liste mit Vorschlägen bestätigen.';

export default defineComponent({
  name: 'SingleLineAddress',
  components: {
    AppFloating,
    AppIcon,
    AddressSelector,
    SdButton,
    sdPopover,
  },
  props: {
    layout: {
      type: String,
      default: 'horizontal',
    },
    target: {
      type: String,
    },
    platform: {
      type: String,
    },
    floatingButton: {
      type: String,
      default: 'Online-Preisrechner',
    },
    noFloatingButton: {
      type: Boolean,
      default: false,
    },
    mTracking: {
      type: String,
    },
  },
  emits: ['open'],
  data() {
    return {
      addressPromise: Promise.resolve(null),
      error: '',
      opening: this.isOpening(),
      funnelLoading: false,
      search: undefined as string | undefined,
      isFloatingButtonCollapsed: false,
      rootElem: null as Element | null,
      isPopoverAbove: false,
      positionPopoverInterval: 0,
      firstMatch: null as unknown as FlatAddress,
    };
  },
  watch: {
    target: {
      handler(newVal) {
        if (newVal) {
          return;
        }
        if (document.readyState === 'complete') {
          this.loadFunnel();
        } else {
          window.addEventListener('load', this.loadFunnel);
        }
      },
      immediate: true,
    },
    hasError(hasError) {
      if (hasError) {
        this.positionPopover();
        this.positionPopoverInterval = window.setInterval(this.positionPopover, 100);
      } else {
        window.clearInterval(this.positionPopoverInterval);
      }
    },
  },
  computed: {
    hasError() {
      return !!this.error;
    },
    withAddress() {
      return true;
    },
  },
  mounted() {
    this.rootElem = this.$el;
    window.addEventListener('hashchange', this.updateOpening);
  },
  unmounted() {
    clearInterval(this.positionPopoverInterval);
  },
  beforeUnmount() {
    window.removeEventListener('hashchange', this.updateOpening);
    window.removeEventListener('load', this.loadFunnel);
  },
  methods: {
    clearError() {
      this.error = '';
    },
    handleNoResultsFound() {
      this.error = errorMessage;
    },
    handleSearchChange(search: string) {
      this.search = search;
      this.clearError();
    },
    isOpening() {
      return !this.target && window.location.hash === '#valuate';
    },
    updateOpening() {
      this.opening = this.isOpening();
    },
    loadFunnel() {
      if (this.funnelLoading) {
        return;
      }
      this.funnelLoading = true;
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.src = `${process.env.VUE_APP_CDN}/${process.env.VUE_APP_VALUATION_RANGE_PATH}`;
      script.async = true;
      document.body.appendChild(script);
    },
    buildAddress(address: AddressItem) {
      const display = address.display_name_token;
      const coordinates = address.coordinates;
      const addressNumber = display.address_no || '';
      const addressNumberExt = display.address_no_ext || '';
      const completeAddressNo = `${addressNumber}${addressNumberExt}`;

      return {
        page: 'ObjectCategory',
        city_name: display.city_name,
        zip: address.postal_code,
        street: display.street_name,
        nr: completeAddressNo,
        id: address.id,
        type: address.type,
        lng: coordinates.lng,
        lat: coordinates.lat,
      };
    },
    openValuationRange(address: FlatAddress | undefined | null, mTracking?: string) {
      if (this.target) {
        let valuationRangeLandingPage = process.env.VUE_APP_VALUATION_RANGE_LANDING_PAGE_IW_WL;

        if (this.platform === 'immonet') {
          valuationRangeLandingPage = process.env.VUE_APP_VALUATION_RANGE_LANDING_PAGE_IN;
        }

        const url = new URL(valuationRangeLandingPage || '');

        if (address) {
          Object.entries(address).forEach(([key, value]) =>
            url.searchParams.set(key, value as string),
          );
        }

        const tracking = this.mTracking
          ? {
              m: mTracking || this.mTracking,
            }
          : {
              utm_medium: 'referral',
              utm_source: window.location.host,
              utm_campaign:
                this.platform === 'immowelt' ? 'iwt_seller_estimation' : 'in_seller_estimation',
              utm_content: window.location.pathname.substring(1),
            };
        Object.entries(tracking).forEach(([key, value]) =>
          url.searchParams.set(key, value as string),
        );

        window.open(url, this.target);
      } else {
        if (address) {
          window.valuationRange = {
            page: 'ObjectCategory',
            displayLocation: address.city_name,
            displayPostalCode: address.zip,
            displayStreet: address.street,
            displayHousenumber: address.nr,
            placeId: address.id,
            geoPoint: {
              longitude: address.lng,
              latitude: address.lat,
            },
          };
        }
        window.location.hash = '#valuate';
      }
    },
    async handleFormSubmit() {
      if (!this.target) {
        this.loadFunnel();
      }

      let address;
      if (this.withAddress) {
        address = await this.addressPromise;
      }

      if (address) {
        this.openValuationRange(address && this.buildAddress(address));
      } else {
        this.openValuationRange(this.firstMatch);
      }
    },
    floatingButtonUpdate(sink: number) {
      const floatingVisible = sink >= 0;
      const mustCollapse = sink >= 550;

      this.isFloatingButtonCollapsed =
        this.layout === 'vertical' &&
        floatingVisible &&
        (this.isFloatingButtonCollapsed || mustCollapse);
    },
    handleButtonClick() {
      this.track('cta-click');
      this.handleFormSubmit();
    },
    handleFloatingButtonClick() {
      this.track('floating-click');
      this.rootElem?.scrollIntoView({ block: 'center', behavior: 'smooth' });

      if (this.search) {
        this.handleFormSubmit();
      }
    },
    handleSelectSubmit(address: AddressItem, mTracking: string) {
      if (address) {
        this.openValuationRange(address && this.buildAddress(address), mTracking);
      }
    },
    handleFirstMatch(address: FlatAddress) {
      this.firstMatch = address;
    },
    positionPopover() {
      const rect = this.$el.getBoundingClientRect();

      const spaceAbove = rect.top - 20;
      const spaceBelow = window.innerHeight - rect.bottom - 20;

      this.isPopoverAbove = spaceBelow >= 50 ? false : spaceAbove > spaceBelow;
    },
  },
});
